import { Component, OnInit, OnDestroy, Renderer2, ViewChild, Inject, DOCUMENT } from '@angular/core';
import { Subscription, Subject } from 'rxjs';
import { CommonFunctions } from './cp/shared/static/common-functions';
import { GlobalsService } from './cp/shared/services/globals.service';
import { KEY_NUM } from './cp/shared/enums/KEY_NUM';
import { Message } from './cp/shared/models/message';
import { MessageType } from './cp/shared/enums/MessageType';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { MsalBroadcastService } from '@azure/msal-angular';
import { CmProAuthService } from './core/services';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { AuthenticationService } from './cp/shared/services/authentication.service';
import { UsersBus } from './cp/shared/models/UsersBus.model';
import { CommonValues } from './cp/shared/static/CommonValues';
import { DQOIReferenceDataQuery } from './cp/dqoi/state/dqoi-reference-data.query';
import { BaseDQOIComponent } from './cp/shared/base-components/base-dqoi.component';
import { DQOIReferenceData } from './cp/shared/models/DQOIReferenceData';
import { environment } from '@env/environment';
import { VersionChanged, VersionCheckService } from './cp/shared/services/versioncheck.service';
import { CPDialogResult } from './cp/shared/enums/CPDialogResult';
import { ConfirmOutput } from './cp/shared/models/ConfirmOutput';
import { ConfirmDialogComponent, ConfirmDialogOptions } from './cp/shared/components/confirm-dialog/confirm-dialog.component';
import { AppClaims } from './cp/shared/models/AppClaims';
import { nameOf } from './core/utils/type-helpers';
import { popoutService } from '@app/cp/shared/services/popout.service';
import { OpenSalesOrderMessage } from './cp/shared/models/OpenSalesOrderMessage';



@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
    private readonly _destroying$ = new Subject<void>();
    isIframe = false;
    isReady = false;
    showPageLoader: boolean = false;
    subs_keyPress: Subscription;
    loadingAccountInfo = false;
    @Inject(DOCUMENT) private document: Document
    @ViewChild('cnfDialog') protected cnfDialog: ConfirmDialogComponent;
    @ViewChild('navigateDialog') protected navigateDialog: ConfirmDialogComponent;

    private versionDialogOpened = false;

    constructor(public globals: GlobalsService,
                private renderer: Renderer2,
                private router: Router,
                private activatedRoute: ActivatedRoute,
                private titleService: Title,
                private authService: CmProAuthService,
                private msalBroadcastService: MsalBroadcastService,
                private authenticationService: AuthenticationService,
                private dqoiReferenceDataQuery: DQOIReferenceDataQuery,
                private versionCheckService: VersionCheckService,
                private popoutService: popoutService )
    {
        this.globals.message$
            .subscribe((msg: Message) => {
                if (msg != null) {
                    if (msg.messageType === MessageType.hotKeyOn) {
                        console.log("globals.message$-hk-on: ", msg);
                        this.setKeyListener(true);
                    } else if (msg.messageType === MessageType.hotKeyOff) {
                        console.log("globals.message$-hk-off: ", msg);
                        this.setKeyListener(false);
                    }
                }
            });
        if (!this.isIframe) {
            this.popoutService.generalMessages$().subscribe(msg => {
                const msgData = new OpenSalesOrderMessage(msg?.data);
                if (msgData.action === 'openSalesOrder' && msgData.salesOrderId && msgData.salesOrderNumber) {
                    this.navigateToZuggitSalesOrderFromCPDispatch(msgData);
                }
            });
        }
        
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            map(() => {
                let child = this.activatedRoute.firstChild;
                const currentUrl = child.snapshot.url.map(segment => segment.path).join('/');
                if (currentUrl.indexOf("auth") > -1 && !this.authService.isEntraLoggedIn()) {
                    this.isReady = true;
                }
                while (child) {
                    if (child.firstChild) {
                        child = child.firstChild;
                    } else if (child.snapshot.data && child.snapshot.data['pageTitle']) {
                        return child.snapshot.data['pageTitle'];
                    } else {
                        return null;
                    }
                }
                return null;
            })
        ).subscribe((data: any) => {
            if (data) {
                this.titleService.setTitle('FJX - ' + data);
            } else {
                this.titleService.setTitle('FieldJetX');
            }
        });

        //we register here so we are always listening for changes to ensure the static _referenceData is updated regardless of modules
        this.dqoiReferenceDataQuery.referenceDataUpdated.subscribe((data: DQOIReferenceData) => {
            if (environment.debug) {
                console.debug("referenceDataUpdated Event Fired", data);
            }
            if (data) {
                this.showPageLoader = false;
                if (!data.isEqualTo(BaseDQOIComponent._referenceData)) {
                    BaseDQOIComponent._referenceData = data;
                }
            }
        });

        this.versionCheckService.versionChangedEvent$.subscribe((versionChanged: VersionChanged) => {
            if (versionChanged.hasNewVersion && !this.versionDialogOpened) {
                var reloadCounter = localStorage.getItem(CommonValues.StorageKeys_ForceReloadCounter);
                let data: any = {};
                const opt = new ConfirmDialogOptions();
                opt.title = "New Version Available";
                if (!reloadCounter || reloadCounter === "0") {
                    opt.message = `A new version (${versionChanged.newVersion}) of the application is available. Click 'Update' to load the latest version.`;
                    data.firstVersionPrompt = true;
                    localStorage.setItem(CommonValues.StorageKeys_ForceReloadCounter, "0");
                    opt.data = data;
                    opt.okButtonText = "Update Now";
                    this.versionDialogOpened = true;
                    this.cnfDialog.showWithOverrides(opt);
                } else {
                    opt.message = `A new version (${versionChanged.newVersion}) of the application is available. We are unable to auto install the new version. Please use CNTRL+F5 to refresh the application.`;
                    data.firstVersionPrompt = false;
                    localStorage.removeItem(CommonValues.StorageKeys_ForceReloadCounter);
                    opt.data = data;
                    opt.okButtonText = "OK";
                    this.versionDialogOpened = true;
                    this.cnfDialog.showWithOverrides(opt);
                }
            }
        });
    }

    private togglePageLoader() {
        this.showPageLoader = !BaseDQOIComponent._referenceData;
    }

    async ngOnInit() {
        this.isIframe = window !== window.parent && !window.opener;
        const isLoggedInUrl = window.location.href.includes("login");
        const isMSALInUrl = window.location.href.includes("msal");
        var externalUrls = ['misc', 'dispatchimages'];
        const isExternalUrl = externalUrls.some(test => window.location.href.includes(test));
        if (isExternalUrl) {
            this.isReady = true;
            return;
        }
        await this.authService.instance.initialize();
        this.authService.instance.enableAccountStorageEvents();
        this.msalBroadcastService.msalSubject$
            .pipe(
                tap((msg: EventMessage) => {
                    if (environment.debug) {
                        console.debug("%c MSAL BroadcastService event", CommonValues.LoggingMessage_ColorPink, msg);
                    }
                }),
                filter(
                    (msg: EventMessage) =>
                        msg.eventType === EventType.LOGIN_SUCCESS ||
                        msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
                        msg.eventType === EventType.SSO_SILENT_SUCCESS ||
                        msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE ||
                        msg.eventType === EventType.ACCOUNT_REMOVED ||
                        msg.eventType === EventType.HANDLE_REDIRECT_END
                ),
                takeUntil(this._destroying$),
            )
            .subscribe((msg: EventMessage) => {
                //NOTE: [dturco 03.19.2025] There is a random scenario happening where user gets stuck on MSAL page even though token is valid. This is a workaround to handle that
                if (this.authService.instance.getAllAccounts().length === 0 && msg.eventType === EventType.HANDLE_REDIRECT_END && !isMSALInUrl) {
                    this.router.navigate(['/login']);
                } else {
                    if (msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.SSO_SILENT_SUCCESS) {
                        this.checkAndSetActiveAccount();
                    }
                    if (msg.eventType === EventType.ACCOUNT_REMOVED) {
                        this.router.navigate(['/login']);
                    }
                    if (msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
                        //this mean we have an expired accessToken and refresh token
                        this.authService.logout(undefined, false).subscribe(() => {
                            this.router.navigate(['/login']);
                        });
                    }
                }

            });

        this.msalBroadcastService.inProgress$
            .pipe(
                filter(
                    (status: InteractionStatus) => {
                        return status === InteractionStatus.None;
                    }
                ),
                takeUntil(this._destroying$),
            )
            .subscribe(() => {
                this.checkAndSetActiveAccount();
            });

        const userAuth = localStorage.getItem(CommonValues.StorageKeys_UserAuth);
        const isUserLoggedIn = this.authService.isEntraLoggedIn() && userAuth;

        if (isLoggedInUrl || isMSALInUrl || isUserLoggedIn || isExternalUrl) {
            this.isReady = true;
            return;
        }
    }
    checkAndSetActiveAccount() {
        if (this.loadingAccountInfo) {
            return;
        }
        this.loadingAccountInfo = true;
        const isMSALInUrl = window.location.href.includes("msal");

        this.togglePageLoader();
        let activeAccount = this.authService.setActiveAccount();
        const ProcessUser = (user: UsersBus, loadedFromCache: boolean): boolean => {
            const usr = new UsersBus(user);
            if (usr.errorMessage === "User Not Found"
               || !usr.hasRole(nameOf<AppClaims>().FieldJetXEnabled)
            ) {
                this.handleLoginError();
                return false;
            } else {
                //NOTE: dont let anything render till we have the user info
                this.authService.parseUserResponse(usr, true, loadedFromCache);
                this.setZuggitCSSClass();
                return true;
            }
        };
        if (activeAccount) {
            const userAuth = localStorage.getItem(CommonValues.StorageKeys_UserAuth);
            if (!userAuth) {
                //After we login from Microsoft.Entra we need to load the User Profile data
                this.authService.getMsalToken().subscribe(
                    {
                        next: result => {
                            this.authenticationService.loginUserInfo(activeAccount.username, result.accessToken).subscribe({
                                next: (user: UsersBus) => {
                                    const processResult = ProcessUser(user, false);
                                    if (processResult) {
                                        //load referenceData. We use always cause these would only fire from a F5 or new login
                                        this.getReferenceDataAfterPageLoad(user, true, () => {
                                            if (isMSALInUrl) {
                                                this.router.navigate(['/dashboard']).then(() => {
                                                    this.isReady = true;
                                                    this.loadingAccountInfo = false;
                                                });
                                            } else {
                                                this.isReady = true;
                                                this.loadingAccountInfo = false;
                                            }
                                            this.versionCheckService.startVersionCheck();
                                        });
                                    }
                                },
                                error: e => {
                                    console.error(e);
                                    if (e.status === 401) {
                                        this.handleLoginError();
                                    } else {
                                        this.handleLoginRedirect();
                                    }
                                }
                            });
                        },
                        error: e => {
                            console.error(e);
                            this.handleLoginRedirect();
                        }
                    });
            } else {
                const currentUser: UsersBus = JSON.parse(userAuth);
                const processResult = ProcessUser(currentUser, true);
                if (processResult) {
                    //NOTE: [dturco 03.19.2025] Dont force a refresh of ref data here. If we have it in IndexDB use it, and if its expired it will still get updated ref data in the background
                    this.getReferenceDataAfterPageLoad(currentUser, false, () => {
                        if (isMSALInUrl) {
                            this.router.navigate(['/dashboard']).then(() => {
                                this.isReady = true;
                                this.loadingAccountInfo = false;
                            });
                        } else {
                            this.isReady = true;
                            this.loadingAccountInfo = false;
                        }
                        this.versionCheckService.startVersionCheck();
                    });
                }
            }
        } else {
            this.isReady = true;
            this.showPageLoader = false;
            this.loadingAccountInfo = false;
            this.versionCheckService.startVersionCheck();
        }
    }

    private getReferenceDataAfterPageLoad(user: UsersBus, forceRefresh: boolean, callback = null) {
        //load referenceData. We use always cause these would only fire from a F5 or new login
        this.dqoiReferenceDataQuery.ensureRefDataIsLoaded(user.companyId, forceRefresh).subscribe(
            {
                next: (wasLoaded: boolean) => {
                    //TODO: maybe use 'wasLoaded' to message the user, and reload the page, or take them to login page again.
                    if (callback) {
                        callback();
                    }
                },
                error: (e) => {
                    console.error(e);
                    if (callback) {
                        callback();
                    }
                }
            });
    }

    confirmVersionUpdateDialog(data: ConfirmOutput) {
        if (data.dialogResult === CPDialogResult.ok && data?.data?.firstVersionPrompt === true) {
            localStorage.setItem(CommonValues.StorageKeys_ForceReloadCounter, "1");
            this.versionCheckService.forceReloadWithNavigation();
        }
        this.versionDialogOpened = false;
    }

    get keyListenerStatus(): boolean {
        return this.keyDownListener != null;
    }
    public keyDownListener: any = null;
    public setKeyListener(state: boolean = null) {
        if (state == true || (state == null && this.keyDownListener == null) && this.renderer) {
            console.log("setKeyListener: ");
            this.keyDownListener = this.renderer.listen(window, "keydown", event => {
                if (event.altKey === true
                    && (event.keyCode === KEY_NUM.ENTER
                        || event.keyCode === KEY_NUM.L
                        || event.keyCode === KEY_NUM.O
                        || event.keyCode === KEY_NUM.D
                        || event.keyCode === KEY_NUM.M
                        || event.keyCode === KEY_NUM.F
                        || event.keyCode === KEY_NUM.R
                        || event.keyCode === KEY_NUM.S)) {
                    console.log("Keydown->keyEvent-in: ", event);
                    event.preventDefault();
                    event.stopPropagation();
                    this.globals.sendKeyPress(event);
                    console.log("key listener event", event);
                }
            });
        }
        else {
            if (this.keyDownListener !== null) {
                this.keyDownListener();
                this.keyDownListener = null;
            }
        }
    }

    handleLoginRedirect() {
        this.router.navigate(['/login']).then(() => {
            this.isReady = true;
            this.loadingAccountInfo = false;
        });
    }

    handleLoginError() {
        this.authService.logout(undefined, false).subscribe(() => {
            this.globals.notifyError(CommonValues.ErrorMessages_AccessDenied, true);
            this.showPageLoader = false;
            this.handleLoginRedirect();
        });
    }

    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
        CommonFunctions.unsubscribe(this.subs_keyPress);
    }

    navigateToZuggitSalesOrderFromCPDispatch(navData: OpenSalesOrderMessage) {
        const opt = new ConfirmDialogOptions();
        opt.title = `Navigate to Sales Order: ${navData.salesOrderNumber}?`;
        opt.message = `Would you like to Navigate to sales order: ${navData.salesOrderNumber}? Any unsaved work on this page will be lost.`;
        opt.okButtonText = "Navigate to Sales Order";
        opt.cancelButtonText = "Cancel";
        opt.data = { dispatchId: navData.salesOrderId, dispatchNumber: navData.salesOrderNumber };
        this.navigateDialog.showWithOverrides(opt);
    }

    confirmNavigateToZuggitSalesOrder(data: ConfirmOutput) {
        if (data.dialogResult === CPDialogResult.ok) {
            this.router.navigate(['/quickview/service/1/' + data.data.dispatchId]);
        }
    }

    setZuggitCSSClass() {
        if (document.body.classList.contains('zuggit-base')){
            return;
        } else if ( this.globals.user.isZuggitUser ) {
            this.renderer.addClass(document.body, 'zuggit-base');
        }
    }
}
